import type { ButtonHTMLAttributes, ReactNode } from "react";
import { menuItemClasses } from "./menu-item-classes";

// Popover / dropdown / sidebar-list row affordance. Left-aligned, full-width,
// soft chip-overlay wash on hover. Distinct from <Button> — it's a row
// affordance, not a standalone action.
//
// One size, one shape, one set of tokens. Every menu/popover/sidebar row in
// the app reads the same. If a context wants something smaller, the answer
// is to fix the surrounding layout, not introduce a size variant here.
//
// `active` (or aria-pressed=true) keeps the wash on without hover —
// for sidebar lists where one item is always "currently selected".
//
// `trailing` adds a right-aligned slot (status dot, count, chevron). The
// row automatically becomes justify-between when trailing is present.
//
// For non-<button> consumers (typically a <Link> or <NavLink>), import
// menuItemClasses() directly from "./menu-item-classes".

interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}

export function MenuItem({
  active = false,
  icon,
  trailing,
  children,
  className,
  type = "button",
  ...props
}: MenuItemProps) {
  return (
    <button
      type={type}
      aria-pressed={active || undefined}
      className={menuItemClasses({ active, hasTrailing: Boolean(trailing), className })}
      {...props}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </button>
  );
}
